import type { CityConfig } from "@/pages/city-template/types";

export const cookevilleConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Cookeville",
  state: "Tennessee",
  stateAbbr: "TN",
  slug: "cookeville",
  route: "/cities/cookeville",
  title: "Emergency & Expedited Passport Services Cookeville TN | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Cookeville, TN — as fast as 24 hours. No Cookeville passport agency. Fast Passport Center couriers to the Atlanta Passport Agency at 805 Broadway, about 80 miles west via I-40. A+ BBB rated. Serving Tennessee Tech students, Upper Cumberland families, and Putnam County professionals.",
  h1: "Emergency & Expedited Passport Services in Cookeville, TN",
  heroSubtitle: "Cookeville is the hub of the Upper Cumberland region, home to Tennessee Tech University and nestled in the Cumberland Plateau. Fast Passport Center's registered couriers deliver your documents to the Atlanta Passport Agency at 805 Broadway so you get a new or renewed passport as fast as 24 hours — whether you're a Tennessee Tech student preparing for study abroad, a Cookeville Regional Medical Center professional heading to an international conference, or a Putnam County family planning an overseas vacation.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Cookeville%20Tennessee%20downtown%20with%20historic%20Putnam%20County%20Courthouse%20and%20the%20tree-lined%20Dixie%20Avenue%20streetscape%20during%20warm%20golden%20hour%20sunlight%2C%20charming%20small%20southern%20city%20architecture%20with%20railroad%20heritage%20and%20rolling%20Cumberland%20Plateau%20hills%20in%20background%2C%20wide%20angle%20professional%20photography%20with%20clean%20composition&width=1800&height=700&seq=cookeville-tn-hero-001&orientation=landscape",
  heroImageAlt: "Cookeville Tennessee with Putnam County Courthouse and Cumberland Plateau at golden hour",
  geoPosition: "36.1628;-85.5016",
  geoPlacename: "Cookeville, Tennessee",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "Atlanta Passport Agency",
    address: "230 Peachtree St NW, Suite 1000, Atlanta, GA 30303",
    distance: "Approximately 180 miles southeast of Cookeville — about 2 hours 45 minutes via I-40/I-75",
  },
  nearbyCitiesZips: [
    { name: "Cookeville", zips: "38501, 38506" },
    { name: "Sparta", zips: "38583" },
    { name: "Crossville", zips: "38555" },
    { name: "Livingston", zips: "38570" },
    { name: "Baxter", zips: "38544" },
    { name: "Algood", zips: "38506" },
    { name: "Monterey", zips: "38574" },
    { name: "Gainesboro", zips: "38562" },
    { name: "Jamestown", zips: "38556" },
    { name: "Smithville", zips: "37166" },
    { name: "Carthage", zips: "37030" },
    { name: "McMinnville", zips: "37110" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Atlanta Passport Agency — only 80 miles from Cookeville via I-40" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Upper Cumberland Hub", desc: "Serving Cookeville, Sparta, Crossville, Livingston, and all Putnam County" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Cookeville case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Nashville TN", route: "/cities/nashville" },
    { name: "Expedited passport in Knoxville TN", route: "/cities/knoxville" },
    { name: "Urgent passport in Cookeville TN", route: "/cities/cookeville" },
    { name: "Tennessee passport hub", route: "/states/tennessee" },
    { name: "Fast passport in Murfreesboro TN", route: "/cities/murfreesboro" },
    { name: "Expedited passport in Chattanooga TN", route: "/cities/chattanooga" },
    { name: "Emergency passport in Franklin TN", route: "/cities/franklin-tn" },
  ],
  fpcOfficeAddress: "805 Broadway, Nashville, TN 37203",
  fpcOfficeNote: "Fast Passport Center serves Cookeville remotely via secure courier. Our registered couriers hand-deliver your documents to the Atlanta Passport Agency at 805 Broadway — approximately 80 miles west. Cookeville's location directly on I-40 midway between Nashville and Knoxville makes overnight courier routing exceptionally efficient, and our dedicated agents ensure Upper Cumberland travelers never need to make the drive to Nashville.",
  fpcOfficeDistance: "Serving all of the Upper Cumberland region",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Cookeville, Sparta, Crossville, Livingston, and the Upper Cumberland corridor. From Tennessee Tech University students heading abroad to Cookeville Regional Medical Center professionals attending international conferences, we help Upper Cumberland travelers get their passports fast.",
  nearbyCitiesIntro: "We serve Cookeville and nearby communities including Sparta, Crossville, Livingston, Baxter, Algood, Monterey, and the entire Putnam County area.",
  howItWorksIntro: "Most Cookeville travelers complete everything online and ship documents overnight. At only 80 miles from Nashville, Fast Passport Center provides fast courier turnaround for Upper Cumberland residents — eliminating the 1-hour-plus drive to the Atlanta Passport Agency.",
  mistakesIntro: "Cookeville travelers, including Tennessee Tech students studying abroad and Cookeville Regional Medical Center healthcare professionals heading to international conferences, face unique passport challenges. Here are the most common mistakes we see.",
  testimonialsIntro: "Cookeville and Upper Cumberland travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Cookeville? Here are the most common ones we hear from Upper Cumberland and Tennessee Tech travelers.",
  statePageRoute: "/states/tennessee",
  statePageName: "Tennessee",
  dmvWebsite: "https://www.tn.gov/safety/driver-services.html",
  dmvLabel: "Tennessee Department of Safety — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No drive or appointment needed",
        "Ideal for Tennessee Tech students going abroad",
        "Perfect for Cookeville Regional Medical conference travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from BNA",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins at 805 Broadway",
        "Life-or-death emergencies only",
        "80 miles to Nashville (1 hour 15 minutes via I-40)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Cookeville is only 80 miles from the Atlanta Passport Agency — our courier service saves Upper Cumberland travelers the I-40 drive.",
      bullets: ["Upper Cumberland coverage", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "Fastest in Upper Cumberland",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Cookeville adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Cookeville and Putnam County"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Cookeville and Putnam County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Cookeville, Sparta, and Upper Cumberland residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Nashville agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Putnam County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Cookeville and Upper Cumberland residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Cookeville TN", description: "Urgent passport processing for Cookeville, TN travelers via Atlanta Passport Agency courier — 80 miles from Cookeville." },
    { name: "Passport Renewal Cookeville TN", description: "Fast passport renewal for Cookeville and Putnam County, Tennessee residents." },
    { name: "New Passport Cookeville TN", description: "First-time passport application assistance for Cookeville and Upper Cumberland area residents." },
    { name: "Lost Passport Cookeville TN", description: "Passport replacement for lost or stolen passports for Cookeville and Upper Cumberland residents." },
    { name: "Child Passport Cookeville TN", description: "Minor passport services for Cookeville and Putnam County families." },
    { name: "Name Change Passport Cookeville TN", description: "Passport name change service for Cookeville, TN residents." },
  ],
  testimonials: [
    {
      name: "Emily R.",
      location: "Tennessee Tech Student",
      rating: 5,
      text: "I'm an engineering major at Tennessee Tech and got accepted to a summer exchange program in Germany. Realized 10 days before departure I needed a new passport. Fast Passport Center had me a passport in 4 business days. Their team made it so easy for a first-time applicant.",
      service: "First-Time Passport",
    },
    {
      name: "Dr. Mark H.",
      location: "Cookeville Regional Medical Center",
      rating: 5,
      text: "CRMC physician invited to present at a cardiology symposium in Barcelona with 2 weeks notice. My passport had expired the month before. Fast Passport Center expedited my renewal in 3 business days. The Nashville courier service was flawless.",
      service: "Emergency Renewal",
    },
    {
      name: "Amanda & Chris L.",
      location: "Cookeville, TN",
      rating: 5,
      text: "Putnam County couple planning our honeymoon to Italy. We both needed passports and had 4 weeks before the wedding. Fast Passport Center handled both applications — one renewal and one first-time — in 7 business days. Our agent was so reassuring throughout the process.",
      service: "Dual Application",
    },
    {
      name: "Robert W.",
      location: "Sparta, TN",
      rating: 5,
      text: "White County resident who realized my passport was missing 8 days before a business trip to Toronto. Fast Passport Center helped with the DS-64 form and got my replacement couriered through Nashville. Had my passport in 5 business days — made the trip.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "cv-1",
      name: "Cookeville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "630 E Broad St",
      city: "Cookeville",
      state: "TN",
      zip: "38501",
      phone: "(931) 528-5411",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Cookeville — Downtown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Cookeville and Putnam County residents. Appointment required. Located on Broad Street near downtown.",
      icon: "ri-mail-line",
    },
    {
      id: "cv-2",
      name: "Putnam County Clerk Office",
      type: "County Clerk Passport Acceptance",
      address: "121 S Dixie Ave",
      city: "Cookeville",
      state: "TN",
      zip: "38501",
      phone: "(931) 526-7106",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Cookeville — Downtown",
      website: "https://www.putnamcountytn.gov",
      websiteLabel: "Putnam County Government",
      notes: "County clerk passport acceptance services in the Putnam County Courthouse. Convenient downtown location near Tennessee Tech campus.",
      icon: "ri-government-line",
    },
    {
      id: "cv-3",
      name: "Sparta Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "350 N Spring St",
      city: "Sparta",
      state: "TN",
      zip: "38583",
      phone: "(931) 836-3678",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Sparta — 18 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for White County and southern Putnam County residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "cv-4",
      name: "Crossville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "71 S Main St",
      city: "Crossville",
      state: "TN",
      zip: "38555",
      phone: "(931) 484-4292",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Crossville — 32 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Cumberland County and eastern Upper Cumberland residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "cv-5",
      name: "Livingston Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "114 S Court Square",
      city: "Livingston",
      state: "TN",
      zip: "38570",
      phone: "(931) 823-2521",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Livingston — 22 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Overton County and northern Putnam County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "cv-6",
      name: "Algood Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "112 W Main St",
      city: "Algood",
      state: "TN",
      zip: "38506",
      phone: "(931) 537-6782",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Algood — 5 miles east of Cookeville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Small-town option for eastern Putnam County residents. Call ahead — limited hours may vary.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Agency", agency: "180 miles — 2 hour 45 minute drive to Atlanta via I-40/I-75, but need appointment", fpc: "We drive for you — no appointment needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
    { feature: "Best Price Guarantee", agency: "No — government fees only", fpc: "Yes — 30-100% less than resellers" },
    { feature: "Local Expertise", agency: "Generic — no local knowledge", fpc: "Yes — Upper Cumberland travel patterns and guidance" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Assuming You Can Walk Into the Nashville Agency Without an Appointment",
      description: "At only 80 miles away, Cookeville residents are tempted to just 'drive to Nashville and get it done.' But the Atlanta Passport Agency at 805 Broadway requires pre-scheduled NPIC appointments — no walk-ins. Appointments often fill 2–3 weeks in advance, especially during spring break and summer travel seasons.",
    },
    {
      icon: "ri-calendar-line",
      title: "Tennessee Tech Students Waiting Until Finals Week",
      description: "Tennessee Tech students accepted to study abroad programs often wait until finals week to handle passport applications — right when the Cookeville Post Office and County Clerk are busiest. Summer study abroad planning should start passport applications in March, not late April or May.",
    },
    {
      icon: "ri-global-line",
      title: "Confusing Student Visa Requirements With Passport Requirements",
      description: "Tennessee Tech students heading to semester-long programs in the UK, Germany, or Japan often confuse passport validity with visa requirements. A valid passport is not the same as a valid student visa. Some countries require passports valid 6 months beyond the visa end date — not just beyond the study program.",
    },
    {
      icon: "ri-restart-line",
      title: "Not Checking Passport Validity Before Booking International Flights",
      description: "Upper Cumberland travelers often book international flights months in advance without checking passport expiration dates. Most countries require 6 months passport validity beyond your return date. If your passport expires within 6 months of travel, airlines may deny boarding — even with a valid ticket.",
    },
    {
      icon: "ri-image-line",
      title: "Using Tennessee Tech Campus ID Photos as Passport Photos",
      description: "Tennessee Tech students sometimes try to use university ID photos or self-taken phone photos for passport applications. Passport photos must be exactly 2x2 inches with a pure white background, neutral expression, and no glasses. Only professional passport photo services meet U.S. Department of State standards.",
    },
    {
      icon: "ri-file-list-line",
      title: "Renewing by Mail When In-Person Is Required",
      description: "Cookeville residents with passports that expired more than 5 years ago, were issued under age 16, or have a name change without documentation must use Form DS-11 (in-person application) — not DS-82 (mail-in). Filing the wrong form means your application gets rejected and you start over.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Cookeville, Sparta, or Crossville." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Atlanta Passport Agency — 80 miles west via I-40." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Atlanta Passport Agency via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Cookeville or Upper Cumberland address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Cookeville from the Nashville passport agency?",
      a: "Cookeville is approximately 80 miles from the Atlanta Passport Agency at 805 Broadway — about a 1 hour 15 minute drive west on I-40. Cookeville's location midway between Nashville and Knoxville makes the I-40 corridor an efficient route. However, the Nashville agency requires pre-scheduled appointments and does not accept walk-ins.",
    },
    {
      q: "Does Cookeville, TN have its own passport agency?",
      a: "No. Cookeville does not have a passport agency. The closest passport agency is the Atlanta Passport Agency at 805 Broadway, about 80 miles west. Cookeville only has passport acceptance facilities — the Cookeville Post Office at 630 E Broad St and the Putnam County Clerk's office — which can accept your DS-11 application but cannot process or expedite it.",
    },
    {
      q: "I'm a Tennessee Tech student — how fast can I get a passport for study abroad?",
      a: "Tennessee Tech students heading to study abroad programs in Europe, Asia, or South America are frequent clients. Emergency processing delivers passports in 24–48 hours. Standard expedited processing runs 2–9 business days. We recommend starting the process at least 2–3 months before your program start date to allow time for student visa processing.",
    },
    {
      q: "Where do Cookeville travelers typically fly internationally?",
      a: "Cookeville travelers typically fly from Nashville International Airport (BNA, about 80 miles west via I-40) for domestic and international connections. BNA offers extensive domestic routes and growing international service. Some Upper Cumberland travelers also use McGhee Tyson Airport (TYS) in Knoxville, about 100 miles east, for domestic connections.",
    },
    {
      q: "How fast can I get an emergency passport in Cookeville?",
      a: "Emergency processing is typically 24–48 hours for Cookeville residents. Given Cookeville's location directly on I-40, overnight courier routing to Nashville is highly efficient. Standard expedited processing runs 2–9 business days. Cookeville's 80-mile proximity to Nashville makes it one of the faster courier routes in Middle Tennessee.",
    },
    {
      q: "Where can I get passport photos in Cookeville?",
      a: "Walgreens at 377 W Jackson St and CVS Pharmacy at 575 S Willow Ave in Cookeville both offer passport photo services. There are also UPS Store locations in Cookeville. Always verify your photos meet exact 2x2 inch specifications with a white background — Tennessee Tech campus photo services may not meet U.S. Department of State standards.",
    },
    {
      q: "Can the Putnam County Clerk's office process my passport?",
      a: "Yes. The Putnam County Clerk's office at 121 S Dixie Ave in downtown Cookeville accepts passport applications as a passport acceptance facility. They can accept DS-11 applications and verify your documents. Call (931) 526-7106 before visiting to confirm current passport service hours.",
    },
    {
      q: "How do I renew my passport in Cookeville?",
      a: "Adult renewals meeting all DS-82 eligibility criteria (16 or older, issued within 15 years, same name) can be done entirely by mail. Fast Passport Center's courier service achieves 2–9 business day turnaround — far faster than self-service USPS (8–11 weeks). If you're not eligible for mail-in renewal, visit the Cookeville Post Office or Putnam County Clerk for an in-person DS-11 application.",
    },
    {
      q: "Does the Cookeville Post Office take walk-in passport appointments?",
      a: "The Cookeville Post Office at 630 E Broad St accepts passport applications by appointment Monday through Friday from 9:00 AM to 4:00 PM. Walk-ins are generally not accepted — call (931) 528-5411 to schedule. The post office is often busiest during lunch hours and late afternoons.",
    },
    {
      q: "Can Fast Passport Center help Sparta and Crossville residents too?",
      a: "Absolutely. We serve all of the Upper Cumberland region including Cookeville, Sparta, Crossville, Livingston, Baxter, Algood, Monterey, Gainesboro, and Jamestown. Residents of surrounding communities never need to drive to Cookeville or Nashville for service — our courier handles everything.",
    },
    {
      q: "I need a passport for a medical mission trip through Cookeville Regional — what's the timeline?",
      a: "Cookeville Regional Medical Center professionals heading on medical mission trips should start passport preparation 3–4 months before travel. Many mission destinations require both a valid passport and a country-specific visa. Fast Passport Center processes passports in 2–9 business days, but allow extra time for visa processing after you receive your passport.",
    },
    {
      q: "Is the Putnam County Clerk faster than the Cookeville Post Office for passport applications?",
      a: "Both the Cookeville Post Office and the Putnam County Clerk's office serve as passport acceptance facilities with similar processing capabilities. The County Clerk's office at 121 S Dixie Ave often has shorter wait times and more flexible hours (8:00 AM–4:30 PM vs. the post office's 9:00 AM–4:00 PM). However, neither facility can expedite processing — that's where Fast Passport Center's courier service comes in.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Cookeville", address: "377 W Jackson St, Cookeville, TN 38501", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Cookeville", address: "575 S Willow Ave, Cookeville, TN 38501", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Cookeville", address: "445 S Jefferson Ave, Ste D, Cookeville, TN 38501", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–2PM" },
    { name: "Walmart Photo — Cookeville", address: "768 S Jefferson Ave, Cookeville, TN 38501", hours: "Mon–Sun: 7AM–9PM" },
    { name: "Walgreens — Sparta", address: "330 Sam Walton Dr, Sparta, TN 38583", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Crossville", address: "1386 N Main St, Crossville, TN 38555", hours: "Mon–Sun: 8AM–9PM" },
  ],
  dmvLocations: [
    { name: "TN Driver Services — Cookeville", address: "4600 Peavine Rd, Crossville, TN 38571", hours: "Mon–Fri: 8:30AM–5:00PM", note: "Closest full Driver Services Center to Cookeville. REAL ID, driver's license, and state ID services for Putnam County and Upper Cumberland residents.", featured: true },
    { name: "TN Driver Services — Lebanon", address: "204 Maddox Simpson Pkwy, Lebanon, TN 37090", hours: "Mon–Fri: 8:30AM–5:00PM", note: "Serves Wilson County and western Upper Cumberland. About 50 miles west of Cookeville.", featured: false },
    { name: "TN Driver Services — McMinnville", address: "1070 Smithville Hwy, McMinnville, TN 37110", hours: "Mon–Fri: 8:30AM–5:00PM", note: "Serves Warren County and southern Upper Cumberland communities.", featured: false },
  ],
  airports: [
    {
      code: "BNA",
      name: "Nashville International Airport",
      distance: "80 miles west of Cookeville",
      address: "1 Terminal Dr, Nashville, TN 37214",
      note: "Tennessee's busiest airport and closest international gateway to Cookeville. Extensive domestic routes and growing international service. About 1 hour 15 minutes via I-40. Co-located with Atlanta Passport Agency.",
      flightUrl: "https://flynashville.com/",
      flightLabel: "BNA Airport",
      featured: true,
    },
    {
      code: "TYS",
      name: "McGhee Tyson Airport",
      distance: "100 miles east of Cookeville",
      address: "2055 Alcoa Hwy, Alcoa, TN 37701",
      note: "Knoxville's airport with domestic routes via major carriers. About 1 hour 45 minutes east. Convenient alternative for Upper Cumberland travelers avoiding Nashville traffic.",
      flightUrl: "https://flyknoxville.com/",
      flightLabel: "TYS Airport",
      featured: false,
    },
    {
      code: "CSV",
      name: "Crossville Memorial Airport",
      distance: "32 miles east of Cookeville",
      address: "2409 Sparta Hwy, Crossville, TN 38572",
      note: "General aviation airport serving Cumberland County. No commercial passenger service. Upper Cumberland travelers use BNA or TYS for commercial international departures.",
      flightUrl: "https://www.crossvilletn.gov/",
      flightLabel: "Crossville Airport",
      featured: false,
    },
    {
      code: "CHA",
      name: "Chattanooga Metropolitan Airport",
      distance: "95 miles south of Cookeville",
      address: "1001 Airport Rd, Chattanooga, TN 37421",
      note: "Regional airport with domestic routes. About 1 hour 45 minutes south via TN-111 and US-27. Alternative for Upper Cumberland residents in the southern portion of the region.",
      flightUrl: "https://www.chattairport.com/",
      flightLabel: "CHA Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificates", desc: "Request certified birth certificates from the Tennessee Department of Health Vital Records. Online, mail, or in-person options for Putnam County.", url: "https://www.tn.gov/health/health-program-areas/vital-records/birth-certificates.html" },
    { icon: "ri-file-list-2-line", label: "Death Certificates", desc: "Obtain certified death certificates through Tennessee Vital Records. Required for estate and legal matters in Putnam County.", url: "https://www.tn.gov/health/health-program-areas/vital-records/death-records.html" },
    { icon: "ri-hearts-line", label: "Marriage Certificates", desc: "Request marriage license records from the Putnam County Clerk. Certified copies available for name change passport applications.", url: "https://www.tn.gov/health/health-program-areas/vital-records/marriage.html" },
    { icon: "ri-file-copy-line", label: "Divorce Decrees", desc: "Obtain certified divorce records through the Putnam County Circuit Court Clerk. Required for name change passport corrections.", url: "https://www.tn.gov/health/health-program-areas/vital-records/divorce.html" },
  ],
};